import { useState, useRef, useEffect, useCallback } from 'react';
import { useCopy, fill } from '../../i18n';
import { useUIState } from '../../contexts/UIStateContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { MODULE_FEATURE_MAP, FEATURE_CONFIG, PLAN_HIERARCHY, getMinPlanForFeature } from '../../utils/featureConfig';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tab } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import PricingPage from '../billing/PricingPage';
import analytics from '../../services/analyticsService';
import {
  GanttChart,
  Columns,
  CalendarDays,
  Calendar,
  ListTodo,
  Table2,
  Network,
  AlertTriangle,
  RefreshCw,
  DollarSign,
  Users,
  Lock,
  ArrowUpCircle,
  UserPlus,
  Settings,
} from 'lucide-react';
import ProjectShareModal from '../modals/ProjectShareModal';

interface UpgradeModalState {
  feature: string;
  displayName: string;
  description: string;
  minPlan: string;
}

interface ModuleTabBarProps {
  /**
   * Abre as Configurações de projeto (dias úteis, duração do dia, feriados).
   *
   * Mora aqui e não no header de um módulo porque é configuração do PROJETO:
   * o calendário governa o cronograma inteiro e a capacidade da equipe, não só
   * o Gantt. Estava preso ao header do Gantt por acidente de origem, o que
   * deixava Kanban, Equipe e Custos sem acesso a ela.
   */
  onOpenSettings: () => void;
}

// Só id e ícone ficam aqui. O rótulo vem do idioma (shell.modules), indexado
// pelo mesmo id usado no roteamento e no MODULE_FEATURE_MAP.
const MODULES = [
  { id: 'gantt', icon: GanttChart },
  { id: 'kanban', icon: Columns },
  { id: 'team', icon: Users },
  { id: 'schedule', icon: CalendarDays },
  { id: 'calendar', icon: Calendar },
  { id: 'todos', icon: ListTodo },
  { id: 'raci', icon: Table2 },
  { id: 'eap', icon: Network },
  { id: 'risks', icon: AlertTriangle },
  { id: 'changes', icon: RefreshCw },
  { id: 'costs', icon: DollarSign },
] as const;

const ModuleTabBar = ({ onOpenSettings }: ModuleTabBarProps) => {
  const t = useCopy().shell;
  const tShell = useCopy().common.appShell;
  const { currentModule, changeModule } = useUIState();
  const { hasFeature, isSystemAdmin, loading: subLoading, planName } = useSubscription();
  const [upgradeModal, setUpgradeModal] = useState<UpgradeModalState | null>(null);
  const [showPricing, setShowPricing] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Scroll fade indicators
  const navRef = useRef<HTMLElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollIndicators = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;
    setCanScrollLeft(nav.scrollLeft > 4);
    setCanScrollRight(nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    updateScrollIndicators();
    nav.addEventListener('scroll', updateScrollIndicators, { passive: true });
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateScrollIndicators);
      ro.observe(nav);
    }
    return () => { nav.removeEventListener('scroll', updateScrollIndicators); ro?.disconnect(); };
  }, [updateScrollIndicators]);

  const isModuleLocked = (moduleId: string) => {
    if (isSystemAdmin || subLoading) return false;
    const featureKey = MODULE_FEATURE_MAP[moduleId];
    if (!featureKey) return false;
    return !hasFeature(featureKey);
  };

  const handleModuleClick = (moduleId: string) => {
    if (isModuleLocked(moduleId)) {
      const featureKey = MODULE_FEATURE_MAP[moduleId] || '';
      const config = FEATURE_CONFIG[featureKey];
      const minPlan = getMinPlanForFeature(featureKey);
      analytics.trackFeatureGateLocked(moduleId, planName || 'free');
      setUpgradeModal({
        feature: featureKey,
        displayName: config?.displayName || moduleId,
        description: config?.description || '',
        minPlan: PLAN_HIERARCHY[minPlan]?.displayName || 'Starter'
      });
      return;
    }
    changeModule(moduleId);
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="relative shrink-0">
      {/* Fade indicators */}
      {canScrollLeft && (
        <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-[var(--color-bg)] to-transparent z-10 pointer-events-none" />
      )}
      {canScrollRight && (
        <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[var(--color-bg)] to-transparent z-10 pointer-events-none" />
      )}
      {/* rolagem-so-com-mouse: a razao NAO e "o dedo nunca chega" (isso seria
          trocar largura por ponteiro, e num tablet touch de 1024px o dedo chega).
          A razao sao os dois gradientes de borda logo acima: eles saem de
          `canScrollLeft`/`canScrollRight`, calculados do `scrollLeft` real, e o
          scroll por toque dispara o mesmo listener. Ou seja, existe afordancia
          visivel independente de ponteiro, que e o que a regra pede. */}
      <nav ref={navRef} data-tour="module-tabs" aria-label={t.nav.ariaTabs} className="flex gap-1 px-4 border-b border-[var(--color-border)] bg-[var(--color-bg)] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {MODULES.map(module => {
          const Icon = module.icon;
          const locked = isModuleLocked(module.id);
          const isActive = currentModule === module.id;
          const aba = (
            <Tab
              key={module.id}
              active={isActive}
              icon={Icon}
              data-testid={`tab-${module.id}`}
              // Cada módulo virou endereço próprio (/p/<projeto>/<módulo>),
              // então a aba ativa é a página corrente e precisa dizer isso.
              // Antes o estado só existia como cor: leitor de tela lia doze
              // abas idênticas, sem nenhuma pista de onde o usuário estava.
              aria-current={isActive ? 'page' : undefined}
              className={cn(locked && "opacity-60")}
              onClick={() => handleModuleClick(module.id)}
            >
              <span>{t.modules[module.id]}</span>
              {locked && <Lock size={12} className="text-[var(--color-text-muted)]" />}
            </Tab>
          );

          // Tooltip só onde ela ACRESCENTA informação, que aqui é o módulo
          // bloqueado (diz qual plano libera). Na aba liberada o balão repetia
          // exatamente o rótulo que já está escrito ao lado do ícone: passar o
          // mouse na barra abria uma caixa preta com a palavra que a pessoa
          // acabou de ler. Repetir o nome acessível também faz o leitor de tela
          // anunciar o mesmo texto duas vezes, como nome e como descrição.
          //
          // O padrão certo já convivia com o errado nesta mesma barra: os dois
          // botões da direita explicam ("Calendário do projeto: dias úteis,
          // horas por dia e feriados"), não repetem.
          if (!locked) return aba;

          return (
            <Tooltip key={module.id}>
              <TooltipTrigger asChild>{aba}</TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{fill(t.nav.lockedHint, { module: t.modules[module.id], plan: PLAN_HIERARCHY[getMinPlanForFeature(MODULE_FEATURE_MAP[module.id] || '')]?.displayName || 'Starter' })}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}

        <div className="ml-auto flex items-center pl-2 border-l border-[var(--color-border)] my-1.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className="py-1.5 px-3 text-[var(--color-text-muted)] text-[13px] font-medium rounded-[var(--radius-sm)] whitespace-nowrap hover:text-[var(--color-text)] hover:bg-[var(--color-bg-hover)]"
                onClick={onOpenSettings}
              >
                <Settings size={15} />
                <span>{t.header.settings}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom"><p>{tShell.settingsTooltip}</p></TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className="py-1.5 px-3 text-[var(--color-text-muted)] text-[13px] font-medium rounded-[var(--radius-sm)] whitespace-nowrap hover:text-[var(--color-text)] hover:bg-[var(--color-bg-hover)]"
                onClick={() => setShowShareModal(true)}
              >
                <UserPlus size={15} />
                <span>{t.header.share}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom"><p>{tShell.shareTooltip}</p></TooltipContent>
          </Tooltip>
        </div>
      </nav>
      </div>

      {/* Modal de upgrade */}
      <Dialog open={upgradeModal !== null} onOpenChange={(open) => !open && setUpgradeModal(null)}>
        <DialogContent className="max-w-sm text-center">
          <div className="w-14 h-14 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center mx-auto mb-4 text-[var(--color-primary)]">
            <Lock size={28} />
          </div>
          <DialogHeader className="text-center">
            <DialogTitle>{upgradeModal?.displayName}</DialogTitle>
            <DialogDescription>{upgradeModal?.description}</DialogDescription>
          </DialogHeader>
          <div className="inline-block px-3 py-1.5 bg-[var(--color-primary)]/10 text-[var(--color-primary)] rounded-full text-sm font-medium">
            {tShell.availableInPlan} <strong>{upgradeModal?.minPlan}</strong>
          </div>
          <DialogFooter className="justify-center gap-3 sm:justify-center">
            <Button variant="outline" onClick={() => setUpgradeModal(null)}>{tShell.close}</Button>
            <Button onClick={() => { analytics.trackPlanUpgradeClick('module-tab-bar'); setUpgradeModal(null); setShowPricing(true); }}>
              <ArrowUpCircle size={16} />
              {tShell.upgrade}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showPricing && <PricingPage onClose={() => setShowPricing(false)} />}
      {showShareModal && <ProjectShareModal onClose={() => setShowShareModal(false)} />}
    </TooltipProvider>
  );
};

export default ModuleTabBar;
